/**
 * The single seat of origin resolution (company / campaign) in the frontend.
 *
 * The list cell and the detail header line are two surfaces that have to agree on what
 * a given record says. A second copy of the collapse rule is exactly how they stop
 * agreeing, so both call in here. Same remedy as `reviewWindow.ts`, same reason, and
 * the backend mirrors it with `origin_view.py`.
 *
 * TWO RULES THAT ARE NOT STYLE:
 *
 * 1. NEVER fall back to an id. Not `company_id`, not `source_campaigns`, not
 *    `"unknown"` -- not even as a last resort "to be helpful". Showing an auditor
 *    `1151` or `3101` does not answer the question they are asking, and the whole
 *    epic exists because a degraded path that prints the id is WORSE than printing
 *    nothing. This module therefore does not read any identifier at all, which is
 *    the only version of that rule that cannot rot. (`"unknown"` needs no special
 *    case here either: it lives on the *id*, which this module never reads.)
 * 2. `undefined` and `[]` are THE SAME CASE. The API omits the key when there is no
 *    datum, but this module must not depend on a client or cache never inventing an
 *    empty list.
 *
 * No new dependencies and no dates: this module moves strings. If you find yourself
 * importing `date-fns` here, you are in the wrong file.
 */

import { Interpretation } from '../types';

/**
 * The dash the list cell shows when there is no company name.
 *
 * A deliberate representation, never an accidentally blank cell: the column header
 * promises a datum on every row, so a row without one has to say so out loud.
 * The DETAIL does not use this -- there, a missing campaign means the line is not
 * painted at all (R9).
 */
export const NO_DATA = '—';

/** How several names are joined so they read as distinct values, not one long name. */
const NAME_SEPARATOR = ' · ';

/**
 * The one collapse rule: a list of origin names to the text that represents it.
 *
 * @param names Raw list from the API, possibly absent, empty, or holding blanks.
 * @returns The names joined for display, or `null` when there is no datum at all.
 *          `undefined`, `[]` and a list of only-blank strings are the same case.
 *          Several names all survive: picking one silently would be a lie.
 */
export function formatOriginNames(names?: string[]): string | null {
  const usable = usableOriginNames(names);
  return usable.length > 0 ? usable.join(NAME_SEPARATOR) : null;
}

/**
 * The names of a raw list that are worth showing, as a list.
 *
 * Extracted from `formatOriginNames` rather than copied: the dropdown of E02-F02 needs
 * the same names the cell shows, but as VALUES and not as one joined string. A second
 * implementation of "which names count" is exactly how the option a auditor picks stops
 * matching the cell they read it from -- the filter would then hide a row that is
 * visibly labelled with the company they chose.
 *
 * @param names Raw list from the API, possibly absent, empty, or holding blanks.
 * @returns The usable names, trimmed. `undefined`, `[]` and a list of only-blank
 *          strings all collapse to `[]` -- rule 2 of this module.
 */
export function usableOriginNames(names?: string[]): string[] {
  if (!names) {
    return [];
  }
  return names
    .filter((name): name is string => typeof name === 'string')
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
}

/**
 * The companies a record belongs to, as values.
 *
 * The dropdown builds its options from here and the filter matches against here, so
 * both agree with `companyCell` BY CONSTRUCTION. Reading `interpretation.company_names`
 * directly at the call site would be the second copy this module exists to prevent.
 *
 * Returns a list and not a single name on purpose: aggregating several TestData can
 * join several companies, and F01 decided to show them all because picking one silently
 * would be a lie. The filter honours that -- a record belongs to EVERY company it names.
 *
 * @param interpretation The record being filtered or listed.
 * @returns Its company names, possibly empty. Never an id (rule 1).
 */
export function companyNames(interpretation: Interpretation): string[] {
  return usableOriginNames(interpretation.company_names);
}

/**
 * The company cell of the interpretations list.
 *
 * @param interpretation The record being rendered.
 * @returns `text` to paint (the names, or the dash) and `title` to offer the full
 *          value on hover when the cell clips it. `title` is undefined when there is
 *          no datum: a tooltip reading "—" would be noise.
 */
export function companyCell(
  interpretation: Interpretation,
): { text: string; title?: string } {
  const names = formatOriginNames(interpretation.company_names);
  if (names === null) {
    return { text: NO_DATA };
  }
  return { text: names, title: names };
}

/**
 * The third line of the detail header.
 *
 * @param interpretation The record being rendered.
 * @returns The campaign text, or `null` when the line must not be rendered AT ALL --
 *          not empty, not a dash, not a reserved gap (R9). The caller is expected to
 *          skip the element entirely on `null`.
 */
export function campaignLine(interpretation: Interpretation): string | null {
  return formatOriginNames(interpretation.source_campaign_names);
}
